import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { GestureInput } from "../audio/types";
import { timbroamAudioEngine } from "../audio/AudioEngine";
import { getInstrumentById, getVariantById } from "../data/instruments";
import { ResponsiveInstrumentRouter } from "../layouts/ResponsiveInstrumentRouter";
import { useTimbroamState } from "../lib/appState";
import {
  getKeyboardPerformanceMappings,
  isPerformanceTextInput
} from "../lib/keyboardPerformance";

export function PlayPage() {
  const { state, dispatch } = useTimbroamState();
  const [audioError, setAudioError] = useState<string | null>(null);
  const [pressedKeys, setPressedKeys] = useState<string[]>([]);
  const voiceIdsByNote = useRef(new Map<string, string>());
  const spaceHoldNote = useRef<string | null>(null);
  const keyboardControls = useRef({
    breathPressure: 0.55,
    bowPressure: 0.5,
    bayanPressure: 0,
    bend: 0.35,
    ombak: 0.45,
    mouthY: 0.35
  });

  const selectedInstrument = useMemo(
    () => getInstrumentById(state.selectedInstrumentId),
    [state.selectedInstrumentId]
  );
  const selectedVariant = useMemo(
    () => getVariantById(state.selectedInstrumentId, state.selectedVariantId),
    [state.selectedInstrumentId, state.selectedVariantId]
  );
  const selectedTuning =
    selectedInstrument.tunings.find((tuning) => tuning.id === state.selectedTuningId) ??
    selectedInstrument.tunings[0];

  const handleNoteStart = useCallback(
    async (gesture: GestureInput) => {
      if (voiceIdsByNote.current.has(gesture.noteName)) {
        return;
      }

      dispatch({ type: "startNote", noteName: gesture.noteName });
      setAudioError(null);

      try {
        const voiceId = await timbroamAudioEngine.playGesture(
          gesture,
          selectedInstrument,
          selectedTuning,
          selectedVariant
        );
        voiceIdsByNote.current.set(gesture.noteName, voiceId);
        dispatch({ type: "setAudioReady", audioReady: true });
      } catch (error) {
        dispatch({ type: "stopNote", noteName: gesture.noteName });
        setAudioError(error instanceof Error ? error.message : "Audio engine failed to start.");
      }
    },
    [dispatch, selectedInstrument, selectedTuning, selectedVariant]
  );

  const handleNoteEnd = useCallback(
    (noteName: string) => {
      const voiceId = voiceIdsByNote.current.get(noteName);

      if (voiceId) {
        timbroamAudioEngine.stopVoice(voiceId);
        voiceIdsByNote.current.delete(noteName);
      }

      dispatch({ type: "stopNote", noteName });
    },
    [dispatch]
  );

  const handleOrnament = useCallback(
    (ornament: string) => {
      const noteName = selectedInstrument.playableNotes[1] ?? selectedInstrument.playableNotes[0];

      void handleNoteStart({ source: "button", noteName, velocity: 0.82, ornament });
      window.setTimeout(() => handleNoteEnd(noteName), 520);
    },
    [handleNoteEnd, handleNoteStart, selectedInstrument.playableNotes]
  );

  const handleControl = useCallback(
    async (controlId: string, value?: number | string | boolean) => {
      setAudioError(null);

      if (value !== undefined) {
        timbroamAudioEngine.setControl(controlId, value);
      }

      try {
        const voiceId = await timbroamAudioEngine.playControl(
          controlId,
          selectedInstrument,
          selectedTuning,
          selectedVariant
        );

        if (voiceId) {
          dispatch({ type: "setAudioReady", audioReady: true });
        }
      } catch (error) {
        setAudioError(error instanceof Error ? error.message : "Audio control failed.");
      }
    },
    [dispatch, selectedInstrument, selectedTuning, selectedVariant]
  );

  useEffect(() => {
    dispatch({ type: "setUiMode", uiMode: "performance" });
  }, [dispatch]);

  useEffect(() => {
    timbroamAudioEngine.stopAll();
    voiceIdsByNote.current.clear();
    setPressedKeys([]);
  }, [state.selectedInstrumentId, state.selectedVariantId, state.selectedTuningId]);

  useEffect(() => {
    const markPressed = (key: string) => {
      setPressedKeys((current) => (current.includes(key) ? current : [...current, key]));
    };

    const markReleased = (key: string) => {
      setPressedKeys((current) => current.filter((pressedKey) => pressedKey !== key));
    };

    const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

    const applyKeyboardControl = (controlId: string, value: number | string | boolean) => {
      void handleControl(controlId, value);
    };

    const nudgeIntensity = (direction: 1 | -1) => {
      const next = clamp01(keyboardControls.current.breathPressure + direction * 0.08);
      keyboardControls.current.breathPressure = next;
      keyboardControls.current.bowPressure = next;
      keyboardControls.current.bayanPressure = next;

      if (selectedInstrument.id === "erhu") {
        applyKeyboardControl("bow_pressure", next);
      } else if (selectedInstrument.id === "tabla") {
        applyKeyboardControl("bayan_pressure", next);
      } else {
        applyKeyboardControl("breath_pressure", next);
      }
    };

    const nudgeBend = (direction: 1 | -1) => {
      const next = clamp01(keyboardControls.current.bend + direction * 0.08);
      keyboardControls.current.bend = next;

      if (selectedInstrument.id === "gayageum") {
        applyKeyboardControl("nonghyeon_depth", next);
      } else if (selectedInstrument.id === "sitar") {
        applyKeyboardControl("sitar_meend_mode", next > 0.72 ? "expert" : next > 0.36 ? "standard" : "beginner");
      } else if (selectedInstrument.id === "yidaki_didgeridoo") {
        keyboardControls.current.mouthY = next;
        applyKeyboardControl("mouth_shape_y", next);
      } else if (selectedInstrument.id === "gamelan_saron") {
        keyboardControls.current.ombak = next;
        applyKeyboardControl("ombak_depth", next);
      } else {
        applyKeyboardControl("micro_bend_amount", next);
      }
    };

    const handleSpaceDown = () => {
      if (spaceHoldNote.current) {
        return;
      }

      if (selectedInstrument.id === "yidaki_didgeridoo") {
        void handleControl("didgeridoo_drone");
      } else if (selectedInstrument.id === "sitar") {
        void handleControl("drone_toggle");
      } else if (selectedInstrument.id === "kora") {
        void handleControl("kora_ostinato");
      } else if (selectedInstrument.id === "janggu") {
        void handleControl("janggu_both_open");
      } else {
        const noteName = selectedInstrument.playableNotes[0] ?? "C4";
        spaceHoldNote.current = noteName;
        void handleNoteStart({ source: "keyboard", noteName, velocity: 0.68, ornament: "hold" });
      }
    };

    const handleNumberMode = (key: string) => {
      const modeIndex = Number(key) - 1;

      if (selectedInstrument.id === "sitar") {
        applyKeyboardControl("sitar_meend_mode", ["beginner", "standard", "expert", "expert"][modeIndex]);
      } else if (selectedInstrument.id === "erhu") {
        applyKeyboardControl("erhu_portamento_shape", modeIndex % 2 === 0 ? "smoothstep" : "logistic");
      } else if (selectedInstrument.id === "gamelan_saron") {
        applyKeyboardControl("saron_tuning_hint", modeIndex % 2 === 0 ? "genericSlendroApprox" : "genericPelogApprox");
      } else {
        applyKeyboardControl("articulation_mode", key);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isPerformanceTextInput(event.target)) {
        return;
      }

      const key = event.key === ";" ? ";" : event.key.toLowerCase();
      const mapping = getKeyboardPerformanceMappings(selectedInstrument).find((item) => item.key === key);

      if (mapping) {
        event.preventDefault();

        if (event.repeat) {
          return;
        }

        markPressed(mapping.key);
        void handleNoteStart({
          source: "keyboard",
          noteName: mapping.noteName,
          velocity: event.shiftKey ? 0.9 : 0.72,
          ornament: event.shiftKey ? mapping.shiftOrnament : mapping.role.replace(/\s+/g, "_").toLowerCase()
        });
        return;
      }

      if (event.key === " ") {
        event.preventDefault();
        markPressed("space");
        if (!event.repeat) {
          handleSpaceDown();
        }
        return;
      }

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        markPressed(event.key);
        nudgeIntensity(event.key === "ArrowUp" ? 1 : -1);
        return;
      }

      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        markPressed(event.key);
        nudgeBend(event.key === "ArrowRight" ? 1 : -1);
        return;
      }

      if (/^[1-4]$/.test(event.key)) {
        event.preventDefault();
        markPressed(event.key);
        handleNumberMode(event.key);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (isPerformanceTextInput(event.target)) {
        return;
      }

      const key = event.key === ";" ? ";" : event.key.toLowerCase();
      const mapping = getKeyboardPerformanceMappings(selectedInstrument).find((item) => item.key === key);

      if (mapping) {
        event.preventDefault();
        markReleased(mapping.key);
        handleNoteEnd(mapping.noteName);
        return;
      }

      if (event.key === " ") {
        event.preventDefault();
        markReleased("space");
        if (spaceHoldNote.current) {
          handleNoteEnd(spaceHoldNote.current);
          spaceHoldNote.current = null;
        }
        return;
      }

      if (event.key.startsWith("Arrow") || /^[1-4]$/.test(event.key)) {
        markReleased(event.key);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      timbroamAudioEngine.stopAll();
    };
  }, [handleControl, handleNoteEnd, handleNoteStart, selectedInstrument]);

  return (
    <ResponsiveInstrumentRouter
      instrument={selectedInstrument}
      variant={selectedVariant}
      tuning={selectedTuning}
      activeNotes={state.activeNotes}
      pressedKeys={pressedKeys}
      audioError={audioError}
      onNoteStart={(gesture) => void handleNoteStart(gesture)}
      onNoteEnd={handleNoteEnd}
      onControl={(controlId, value) => void handleControl(controlId, value)}
      onOrnament={handleOrnament}
    />
  );
}
